import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import QuestionsContainer from '../QuestionsContainer';
import GroupParentDisplay from '../GroupParentDisplay';
import ProfileSuggestionModal from '../../ProfileSuggestionModal';
import rankedTagImg from '../../../../assets/images/icons/ranked_tag.svg';
import { date } from '../../../dateUtils';
import { orderByStartDate } from '../../../arrayUtils';
import './AnswerRenderer.scss';

const renderValue = (value) => {
  if (value) {
    if (Array.isArray(value)) {
      return (
        <ul>
          {value.map((item) => (
            <li className="fs-hide" key={item.label}>{item.label}</li>
          ))}
        </ul>
      );
    }
    return <span className="fs-hide">{value.label || value}</span>;
  }

  return null;
};

const rankedTagTooltip = (
  <Tooltip className="proficiency-ranking-tag-tooltip" id="proficiency-ranking-tooltip">
    Marked as top area by consultant
  </Tooltip>
);

const renderTag = (tag) => {
  const proficiency = tag.proficiency ? ` (${tag.proficiency})` : '';
  const tagLabel = `${tag.label}${proficiency}`;

  if (tag.ranking) {
    return (
      <OverlayTrigger placement="top" overlay={rankedTagTooltip} key={`${tag.label}-overlay`}>
        <span key={tag.label} className="badge body-text-3 me-2 proficiency-ranking-tag">
          <img
            src={rankedTagImg}
            alt="Top Tag"
            height="15"
            width="10"
          />
          {tagLabel}
        </span>
      </OverlayTrigger>
    );
  }

  return (
    <span key={tag.label} className="badge body-text-3 me-2 text-break">
      {tagLabel}
    </span>
  );
};

const answerIdWithParent = (answer) => (
  answer.parent_id ? [answer.parent_id, answer.id] : [answer.id]
);

class AnswerRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isHovering: false,
      showReadMore: false,
      showModal: false,
      editableIds: [],
      objectId: null,
      paginatedQuestionIds: [],
      truncateAnswers: props.truncateAnswers,
      pastRolesResData: [],
      forceRefreshKey: 0,
    };

    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleAppendTags = this.handleAppendTags.bind(this);
    this.handleClearSuggestions = this.handleClearSuggestions.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.setEditableIds = this.setEditableIds.bind(this);
    this.handlePastRolesAfterUpdate = this.handlePastRolesAfterUpdate.bind(this);
  }

  componentDidMount() {
    this.setShowReadMore();
  }

  setShowReadMore() {
    const { answer } = this.props;
    // Added 1 to clientHeight because in Safari 12.1 there is a rounding error leading
    // scrollHeight to be bigger than the clientHeight by 1 and so showReadMore is true
    if (answer.style === 'text_area') {
      this.setState({
        showReadMore: this.textAreaElement
        && this.textAreaElement.scrollHeight > this.textAreaElement.clientHeight + 1,
      });
    }
  }

  setEditableIds(objectId = null) {
    const { answer } = this.props;
    const editableIds = answerIdWithParent(answer);
    const paginatedQuestionIds = this.buildPaginatedIdsList(editableIds);
    this.setState({
      editableIds, objectId, paginatedQuestionIds, showModal: true,
    });
  }

  // resulting has the shape: [ [firstPageQuestions...], [secondPageQuestions...], ... ]
  buildPaginatedIdsList(firstPageQuestions) {
    const { paginated, childrenQuestionIds } = this.props;
    if (paginated) {
      const paginatedQuestionIds = [];
      // currently assumes one page per child question.
      paginatedQuestionIds.push(firstPageQuestions);
      childrenQuestionIds.forEach((q) => paginatedQuestionIds.push([q]));
      return paginatedQuestionIds;
    }
    return [];
  }

  handleCloseModal() {
    const { closeModalCallback, answer } = this.props;

    this.setState({ showModal: false }, () => { closeModalCallback(answer); });
  }

  handleMouseOver() {
    this.setState({ isHovering: true });
  }

  handleMouseLeave() {
    this.setState({ isHovering: false });
  }

  handleAppendTags() {
    const { answer, updateSuggestedTags } = this.props;

    this.handleCloseModal(answer);
    updateSuggestedTags([], answer.association_model);
  }

  handleClearSuggestions(tagIds) {
    const { updateSuggestedTags, suggestedTags, answer } = this.props;
    const filteredTags = suggestedTags.filter((tag) => !(tagIds.includes(tag.id)));

    updateSuggestedTags(filteredTags, answer.association_model);
  }

  handlePastRolesAfterUpdate(data) {
    // this will update past roles view after edit and update
    const { forceRefreshKey } = this.state;
    this.setState({ pastRolesResData: data, forceRefreshKey: forceRefreshKey + 1 });
  }

  renderEdit(objectId = null, renderAddQuestionKey = false) {
    const {
      answer, fullView, editMode, suggestedTags,
    } = this.props;
    const { isHovering } = this.state;

    const isPastRole = answer.question_type === 'past_roles';

    if (fullView && renderAddQuestionKey) {
      return (
        <a
          className={`edit-attribute modal-anchor-trigger add-answer medium-weight ${isPastRole ? 'body-text-2' : 'body-text-3'}`}
          tabIndex={0}
          role="button"
          onClick={() => this.setEditableIds()}
        >
          <i className="fa fa-plus" />
          {`Add ${isPastRole ? 'Role' : answer.question_key}`}
        </a>
      );
    } if (fullView && (isHovering || editMode) && suggestedTags.length === 0) {
      return (
        <a
          className="edit-attribute"
          tabIndex={0}
          role="button"
          onClick={() => this.setEditableIds(objectId)}
        >
          Edit
        </a>
      );
    }
    return null;
  }

  renderDisplayFreeText(questionKey, answer) {
    const { showReadMore } = this.state;
    const { isReadingMore } = this.props;

    return (
      <div className={`${questionKey}-answer-container`}>
        <h2 className={`mb-4 ${questionKey}`}>
          {questionKey}
          <span className="body-text-2">{answer && this.renderEdit()}</span>
        </h2>
        <div
          className={`fs-hide body-text-2 text-read-more ${isReadingMore ? 'show' : 'overflow'}`}
          ref={(el) => { this.textAreaElement = el; }}
        >
          {(questionKey === 'Linkedin' && answer)
            ? (
              <a href={(/^https?:\/\//i.test(answer)) ? answer : `https://${answer}`} target="_blank" rel="noreferrer">
                Visit LinkedIn Profile
              </a>
            )
            : answer}
        </div>
        {!answer && this.renderEdit(null, true)}
        {showReadMore
          && (
          <div className="align">
            <a
              className="body-text-2"
              tabIndex={0}
              role="link"
              onClick={this.readMore}
            >
              {isReadingMore ? 'Read less' : 'Read more'}
            </a>
          </div>
          )}
      </div>
    );
  }

  renderDisplayResume(questionKey, answer) {
    return (
      <div>
        <h2 className={`mb-4 ${questionKey}`}>
          {questionKey}
          <span className="body-text-2">{answer && this.renderEdit()}</span>
        </h2>
        <div className="resume-name mb-4">
          <a href={`/resumes/${answer.resource_id}`} target="_blank" rel="noreferrer">
            {answer.document_file_name}
          </a>
        </div>
      </div>
    );
  }

  renderDisplayManyTags(questionKey, answer, modelName) {
    const {
      csrfToken, editMode, showSuggestionModal, fullView,
    } = this.props;
    const { suggestedTags } = this.props;

    if (Array.isArray(answer)) {
      if (!fullView && answer.length === 0) {
        return null;
      }
      return (
        <div>
          <div className={`medium-weight ${answer.length === 0 && 'mb-2'}`}>
            {questionKey}
            {fullView && suggestedTags.length > 0
              && (
              <ProfileSuggestionModal
                tags={suggestedTags}
                csrfToken={csrfToken}
                appendTags={this.handleAppendTags}
                clearTags={this.handleClearSuggestions}
                answerState={answer}
                editMode={editMode}
                forceShow={showSuggestionModal === modelName}
              />
              )}
            {answer.length > 0 && this.renderEdit()}
          </div>
          {answer.map((tag) => renderTag(tag))}
          {answer.length === 0 && this.renderEdit(null, true)}
        </div>
      );
    }
    return (
      <div>
        <div className="title medium-weight mb-4 mute">
          {questionKey}
          {answer.length > 0 && this.renderEdit()}
        </div>
        <span className="body-text-3 mute">{answer}</span>
      </div>
    );
  }

  renderList(questionKey, answer, isReadingMore) {
    const { readingMoreThreshhold } = this.props;

    if (answer.length === 0) {
      return (
        <div className="body-text-2">
          <h2 className="mb-2">{questionKey}</h2>
          {this.renderEdit(null, true)}
        </div>
      );
    }
    let answ = answer;
    if (!isReadingMore) { answ = answer.slice(0, readingMoreThreshhold); }
    return (
      <div className="list body-text-2">
        <h2 className="mb-4">
          {questionKey}
          <span className="body-text-2">{this.renderEdit()}</span>
        </h2>
        <ul>
          {answ.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }

  renderDisplayValue(questionKey, answer) {
    return (
      <div>
        <div className="title medium-weight">
          {questionKey}
          {this.renderEdit()}
        </div>
        {renderValue(answer)}
      </div>
    );
  }

  renderDisplayNumericRange(questionKey, answer) {
    if (!answer.low && !answer.high) {
      return (
        <div>
          <div className="title medium-weight">
            {questionKey}
          </div>
          {this.renderEdit(null, true)}
        </div>
      );
    }
    return (
      <div>
        <div className="title medium-weight">
          {questionKey}
          {this.renderEdit()}
        </div>
        {(answer.low.length > 0 && answer.high.length > 0) ? `$${answer.low} - $${answer.high}/hr` : `$${answer.low}${answer.high}/hr`}
      </div>
    );
  }

  renderNullAnswer(questionKey) {
    return (
      <div>
        <h2 className={`mb-4 ${questionKey}`}>
          {questionKey}
        </h2>
        {this.renderEdit(null, true)}
      </div>
    );
  }

  renderPastRoles(answer) {
    const {
      fullView,
      isReadingMore,
      readingMoreThreshhold,
    } = this.props;
    const { pastRolesResData } = this.state;

    if (answer.length === 0) {
      return (
        <div className="body-text-2">
          <h2 className="mb-2">Past Roles</h2>
          {this.renderEdit(null, true)}
        </div>
      );
    }
    let orderedPastRoles = orderByStartDate(pastRolesResData.length ? pastRolesResData : answer);
    if (!isReadingMore) { orderedPastRoles = orderedPastRoles.slice(0, readingMoreThreshhold); }

    return (
      <div className="body-text-2">
        <h2 className="mb-4">
          <span className="past-roles-title me-3">Past Roles</span>
          {this.renderEdit(null, true)}
        </h2>
        <div className="mt-3">
          {orderedPastRoles.map((pastRole) => (
            <div key={pastRole.id} className="past-role">
              <div className="fs-hide medium-weight">
                {pastRole.title}
                {this.renderEdit(pastRole.id)}
              </div>
              <div className="fs-hide">
                {pastRole.role}
              </div>
              <div className="fs-hide">
                {pastRole.company_name}
              </div>
              { isReadingMore
                && (
                <div className="fs-hide body-text-2 text-read-more show">
                  {pastRole.description}
                </div>
                )}
              {!answer && this.renderEdit(null, true)}
              {fullView
                ? (
                  <div className="mute fs-hide">
                    {date(pastRole.start_date, pastRole.end_date, pastRole.current)}
                  </div>
                )
                : (
                  <div className="mute fs-hide">
                    {pastRole.experience}
                  </div>
                )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderGroupParent(questionKey, answer, isReadingMore) {
    if (answer.length === 0) {
      return (
        <div className="body-text-2">
          <h2 className="mb-2">{questionKey}</h2>
          {this.renderEdit(true)}
        </div>
      );
    } if (answer === 'Not Applicable') {
      return (
        <div className="body-text-3 mute">
          <h2 className="mb-3">
            {questionKey}
            <span className="body-text-2">{this.renderEdit()}</span>
          </h2>
          Not Applicable
        </div>
      );
    }
    let answ = answer;
    if (!isReadingMore) { answ = answer.slice(0, 2); }
    return (
      <div className="body-text-2">
        <h2 className="mb-4">
          {questionKey}
          <span className="body-text-2">{this.renderEdit()}</span>
        </h2>
        <GroupParentDisplay
          answers={answ}
          questionKey={questionKey}
          key={questionKey}
        />
      </div>
    );
  }

  renderAnswer() {
    const { isReadingMore, answer } = this.props;
    if (answer.answer === null) {
      return this.renderNullAnswer(answer.question_key);
    }
    switch (answer.question_type) {
      case 'select_many':
      case 'select_one':
      case 'only_custom_tags':
        if (answer.style === 'radio_oneline') {
          return this.renderDisplayValue(
            answer.question_key,
            answer.answer,
          );
        }
        return this.renderDisplayManyTags(
          answer.question_key,
          answer.answer,
          answer.association_model,
        );
      case 'freetext':
      case 'location':
        return this.renderDisplayFreeText(
          answer.question_key,
          answer.answer,
        );
      case 'numeric_range':
        return this.renderDisplayNumericRange(
          answer.question_key,
          answer.answer,
        );
      case 'file':
        if (answer.style === 'resume') {
          return this.renderDisplayResume(
            answer.question_key,
            answer.answer,
            answer.resume_url,
          );
        }
        break;
      case 'past_roles':
        return this.renderPastRoles(answer.answer);
      case 'list':
        return this.renderList(
          answer.question_key,
          answer.answer,
          isReadingMore,
        );
      case 'group_parent':
        return this.renderGroupParent(
          answer.question_key,
          answer.answer,
          isReadingMore,
        );
      default:
        return null;
    }
    return null;
  }

  render() {
    const {
      editable,
      fullView,
      hideFooterButton,
      questionnaireName,
      questionableId,
      questionableType,
      csrfToken,
      answer,
      paginated,
      handleNewResume,
      isReadingMore,
    } = this.props;

    const {
      showModal, paginatedQuestionIds, editableIds, objectId, forceRefreshKey,
    } = this.state;
    const answerDisplay = (
      (!fullView
        && (answer.answer === null || answer.answer.length === 0)) ? null
        : (
          <div
            className="answer full-width body-text-2"
            onMouseOver={this.handleMouseOver}
            onMouseLeave={this.handleMouseLeave}
          >
            {this.renderAnswer()}
          </div>
        )
    );

    if (editable) {
      return (
        <div className="answer full-width">
          { answerDisplay }
          <QuestionsContainer
            key={editableIds.join('+') + forceRefreshKey}
            questionnaireName={questionnaireName}
            questionIds={editableIds}
            objectId={objectId}
            questionType={answer.question_type}
            questionableId={questionableId}
            questionableType={questionableType}
            csrfToken={csrfToken}
            modalOpen={showModal}
            hideFooterButton={hideFooterButton || answer.question_type === 'group_parent'}
            withModal
            paginated={paginated}
            paginatedQuestionIds={paginatedQuestionIds}
            closeModalCallback={this.handleCloseModal}
            handleNewResume={handleNewResume}
            forceCloseButton={answer.question_type === 'past_roles'}
            isReadingMore={isReadingMore}
            handlePastRolesAfterUpdate={this.handlePastRolesAfterUpdate}
          />
        </div>
      );
    }
    return answerDisplay;
  }
}

AnswerRenderer.propTypes = {
  answer: PropTypes.shape({}).isRequired,
  fullView: PropTypes.bool,
  editMode: PropTypes.bool,
  isReadingMore: PropTypes.bool,
  suggestedTags: PropTypes.arrayOf(PropTypes.shape({})),
  updateSuggestedTags: PropTypes.func,
  csrfToken: PropTypes.string,
  showSuggestionModal: PropTypes.string,
  questionableType: PropTypes.string,
  questionableId: PropTypes.number,
  closeModalCallback: PropTypes.func,
  questionnaireName: PropTypes.string,
  hideFooterButton: PropTypes.bool,
  editable: PropTypes.bool,
  paginated: PropTypes.bool,
  childrenQuestionIds: PropTypes.arrayOf(PropTypes.number),
  handleNewResume: PropTypes.func,
  truncateAnswers: PropTypes.bool,
  readingMoreThreshhold: PropTypes.number,
};

AnswerRenderer.defaultProps = {
  fullView: false,
  updateSuggestedTags: () => null,
  editMode: false,
  isReadingMore: true,
  suggestedTags: [],
  csrfToken: null,
  showSuggestionModal: '',
  editable: false,
  childrenQuestionIds: [],
  paginated: false,
  handleNewResume: () => {},
  truncateAnswers: false,
  readingMoreThreshhold: 2,
};

export default AnswerRenderer;
